/*
  product.js: product page and quick-add modal.
  <product-info>   variant changes (re-renders data-update="…" parts via the Section Rendering API), add to cart,
                   Buy now (with the order-limit check), "N in cart", custom-field counters, share, recently viewed.
  <product-gallery> thumbnails, counter, lightbox zoom, click-to-play video, jump to variant image.
  <sticky-atc>     bar that appears when the main buttons scroll away; submits the real product form.
  <product-tabs>   desktop tabs over the phone accordion.
  <product-recommendations>  related and cross-sell (complementary) products loaded after the page.
  window.theme.openQuickView(handle): quick-add modal for "Choose options" on cards.
*/
const { config, money, t, publish, subscribe, toast, addToCart, fetchJSON, setTabbar, safeGet, safeSet } = window.theme;

/* ======================= product info ======================= */
class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.formId = this.dataset.form;
    try { this.product = JSON.parse(this.querySelector('[data-product-json]')?.textContent || 'null'); } catch (e) { this.product = null; }
    this.root = this.dataset.quick !== undefined ? this : (this.closest('section') || this);

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-option]')) this.onOptionChange();
      if (e.target.matches('[data-variant-select]')) this.selectVariant(Number(e.target.value));
    });
    this.addEventListener('input', (e) => {
      const ta = e.target.closest('[data-counter]');
      if (ta) {
        const c = this.querySelector(`[data-count-for="${ta.id}"]`);
        if (c) c.textContent = `${ta.value.length} / ${ta.maxLength}`;
      }
    });
    this.addEventListener('click', (e) => this.onClick(e));
    // volume tiers: highlight the tier the chosen quantity reaches
    this.root.addEventListener('change', (e) => { if (e.target.closest('qty-stepper')) this.updateTiers(); });
    this.root.addEventListener('input', (e) => { if (e.target.closest('qty-stepper')) this.updateTiers(); });
    this.updateTiers();
    this.addEventListener('pointerenter', (e) => {
      const a = e.target.closest?.('[data-prefetch]');
      if (a && !a.dataset.prefetched) {
        a.dataset.prefetched = '1';
        const l = document.createElement('link'); l.rel = 'prefetch'; l.href = a.href; document.head.appendChild(l);
      }
    }, true);

    const native = this.querySelector('[data-share="native"]');
    if (native && navigator.share) native.hidden = false;

    this.unsubCart = subscribe('cart:updated', (cart) => this.updateInCart(cart));
    if (this.dataset.quick === undefined) this.rememberViewed();
  }

  disconnectedCallback() { this.unsubCart?.(); }

  get form() { return document.getElementById(this.formId); }

  currentVariantId() {
    const sel = this.root.querySelector(`select[name="id"][form="${this.formId}"]`);
    if (sel) return Number(sel.value);
    const hidden = this.form?.querySelector('input[name="id"]');
    return Number(hidden?.value);
  }

  onOptionChange() {
    if (!this.product) return;
    const groups = [...this.querySelectorAll('.c-variants__group')];
    const selected = groups.map((g) => {
      const checked = g.querySelector('input[data-option]:checked');
      const select = g.querySelector('select[data-option]');
      return checked ? checked.value : select?.value;
    });
    const exact = this.product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
    // when the exact combination doesn't exist, keep the changed option and pick the best available match
    const v = exact || this.product.variants.find((x) => x.available && x.options[0] === selected[0]) || this.product.variants[0];
    this.selectVariant(v.id);
  }

  async selectVariant(id) {
    const v = this.product?.variants.find((x) => x.id === id);
    if (!v) return;
    const sel = this.root.querySelector(`select[name="id"][form="${this.formId}"]`);
    if (sel) sel.value = id;
    if (this.dataset.quick === undefined) {
      const u = new URL(location.href); u.searchParams.set('variant', id);
      history.replaceState({}, '', u);
    }
    if (v.media) this.root.querySelector('product-gallery')?.jumpTo(v.media);
    await this.renderVariant(id);
    publish('variant:change', { variant: v, sectionId: this.sectionId });
  }

  async renderVariant(id) {
    const qty = this.root.querySelector('qty-stepper input')?.value;
    const focused = document.activeElement;
    const focusKey = focused && this.contains(focused) && focused.name ? `${focused.name}|${focused.value}` : null;
    this.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${this.dataset.url}?variant=${id}&section_id=${this.sectionId}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      this.root.querySelectorAll('[data-update]').forEach((el) => {
        const fresh = doc.querySelector(`[data-update="${el.dataset.update}"]`);
        if (fresh) el.replaceWith(document.importNode(fresh, true));
      });
      const stepper = this.root.querySelector('qty-stepper');
      if (stepper && qty) stepper.setValue(Number(qty));
      this.updateTiers();
      if (focusKey) {
        const [name, value] = focusKey.split('|');
        [...this.querySelectorAll(`[name="${CSS.escape(name)}"]`)].find((el) => el.value === value)?.focus({ preventScroll: true });
      }
      window.theme.currency?.refresh?.(this.root);
    } catch (e) {
      location.href = `${this.dataset.url}?variant=${id}`;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  updateTiers() {
    // pre-order on WhatsApp: [qty] in the message follows the quantity stepper
    const qtyNow = Number(this.root.querySelector('qty-stepper input')?.value) || 1;
    this.root.querySelectorAll('[data-wa-template]').forEach((a) => {
      a.href = `https://wa.me/${a.dataset.waNumber}?text=${encodeURIComponent(a.dataset.waTemplate.split('[qty]').join(qtyNow))}`;
    });
    const box = this.root.querySelector('[data-tiers]');
    if (!box) return;
    const qty = Number(this.root.querySelector('qty-stepper input')?.value) || 1;
    let best = null;
    box.querySelectorAll('[data-tier-qty]').forEach((b) => { if (qty >= Number(b.dataset.tierQty)) best = b; });
    box.querySelectorAll('[data-tier-qty]').forEach((b) => b.setAttribute('aria-pressed', b === best ? 'true' : 'false'));
  }

  async onClick(e) {
    const tier = e.target.closest('[data-tier-qty]');
    if (tier) {
      this.root.querySelector('qty-stepper')?.setValue(Number(tier.dataset.tierQty));
      this.updateTiers();
      return;
    }
    const share = e.target.closest('[data-share]');
    if (share) {
      if (share.dataset.share === 'copy') {
        // the link, or the whole share message when the block says "Copy link includes the message"
        const text = share.dataset.copyText || share.dataset.url;
        try { await navigator.clipboard.writeText(text); toast(config.strings.linkCopied); } catch (err) { /* ignore */ }
      } else if (navigator.share) {
        const data = { title: share.dataset.title, url: share.dataset.url };
        if (share.dataset.text) data.text = share.dataset.text; // the share message; the link goes in url
        navigator.share(data).catch(() => {});
      }
    }
  }

  async submit(e) {
    const form = this.form;
    const submitter = e.submitter;
    const buyNow = submitter?.name === 'buy_now';
    const err = this.root.querySelector('[data-buy-error]');
    if (err) err.hidden = true;

    if (!form.checkValidity()) {
      e.preventDefault();
      [...form.elements].filter((el) => !el.checkValidity()).forEach((el) => el.classList.add('is-invalid'));
      form.reportValidity();
      return;
    }
    // file uploads need a normal multipart post (Shopify), so let the browser submit the form
    const hasFile = [...form.elements].some((el) => el.type === 'file' && el.files?.length);
    if (hasFile) return;

    e.preventDefault();
    const btn = buyNow ? submitter : (this.root.querySelector('[data-add-to-cart]') || submitter);
    btn?.classList.add('is-loading');
    try {
      const fd = new FormData(form);
      if (buyNow) {
        await addToCart(fd, btn, { silent: true });
        const cart = await fetchJSON(`${config.routes.cart}.js`);
        const problem = window.theme.cartLimitProblem?.(cart);
        if (problem && problem.blocking) {
          // show the cart with the order-rule message instead of going to checkout
          const drawer = document.querySelector('cart-drawer');
          if (drawer) { await drawer.refresh(); drawer.open(btn); } else { location.href = config.routes.cart; }
        } else {
          location.href = '/checkout';
        }
      } else {
        await addToCart(fd, btn);
      }
    } catch (error) {
      if (err) { err.textContent = error.message || config.strings.error; err.hidden = false; }
    } finally {
      btn?.classList.remove('is-loading');
    }
  }

  updateInCart(cart) {
    const id = this.currentVariantId();
    const el = this.root.querySelector('[data-in-cart]');
    if (!el || !cart) return;
    const n = cart.items.filter((i) => i.variant_id === id).reduce((s, i) => s + i.quantity, 0);
    el.textContent = t(config.strings.inCart, { count: n });
    el.hidden = n === 0;
  }

  rememberViewed() {
    if (!this.product) return;
    let list = [];
    try { list = JSON.parse(safeGet(localStorage, 'recently-viewed') || '[]'); } catch (e) { list = []; }
    const p = this.product;
    list = [{ handle: p.handle, title: p.title, url: p.url, img: p.image }, ...list.filter((i) => i.handle !== p.handle)].slice(0, 12);
    safeSet(localStorage, 'recently-viewed', JSON.stringify(list));
  }
}
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

/* product form submit (buttons sit outside the <form> and use form="…") */
document.addEventListener('submit', (e) => {
  const form = e.target.closest('form[data-product-form]');
  if (!form) return;
  // note: form.id would return the <input name="id"> inside the form, so read the attribute
  const info = document.querySelector(`product-info[data-form="${form.getAttribute('id')}"]`);
  if (info) info.submit(e);
});

/* ======================= gallery ======================= */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.counter = this.querySelector('[data-gallery-current]');
    this.lightbox = this.querySelector('[data-lightbox]');
    this.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-thumb]');
      if (thumb) { this.goTo(Number(thumb.dataset.thumb)); return; }
      const zoom = e.target.closest('[data-zoom]');
      if (zoom) { this.openLightbox(Number(zoom.dataset.zoom)); return; }
      const play = e.target.closest('[data-video-embed]');
      if (play) {
        const f = document.createElement('iframe');
        f.src = play.dataset.videoEmbed; f.allow = 'autoplay; encrypted-media; picture-in-picture'; f.allowFullscreen = true; f.title = play.getAttribute('aria-label');
        const wrap = document.createElement('div'); wrap.className = 'c-gallery__video'; wrap.appendChild(f);
        play.replaceWith(wrap);
        return;
      }
      if (e.target.closest('[data-lightbox-close]')) { this.lightbox.close(); return; }
      const lbImg = e.target.closest('.c-lightbox__slide img');
      if (lbImg) lbImg.parentElement.classList.toggle('is-zoomed');
    });
    let raf;
    this.track?.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (this.counter) this.counter.textContent = i + 1;
        this.thumbs.forEach((th, n) => th.setAttribute('aria-current', n === i ? 'true' : 'false'));
        this.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }, { passive: true });
  }
  goTo(i, smooth = true) {
    const slide = this.track?.children[i];
    if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  }
  jumpTo(mediaId) {
    const slide = this.track?.querySelector(`[data-media-id="${mediaId}"]`);
    if (slide) this.goTo(Number(slide.dataset.index));
  }
  openLightbox(index) {
    if (!this.lightbox) return;
    this.lightbox.querySelectorAll('img[data-src]').forEach((img) => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
    this.lightbox.showModal();
    const slide = this.lightbox.querySelector(`.c-lightbox__slide[data-index="${index}"]`);
    const track = this.lightbox.querySelector('[data-lightbox-track]');
    if (slide) track.scrollLeft = slide.offsetLeft;
  }
}
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

/* ======================= sticky add to cart ======================= */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector(`product-info[data-form="${this.dataset.form}"] [data-buy]`);
    if (!target) return;
    this.hidden = false;
    const footer = document.querySelector('.c-footer');
    let pastButtons = false, atFooter = false;
    const update = () => {
      const show = pastButtons && !atFooter;
      this.classList.toggle('is-visible', show);
      setTabbar(!show, 'sticky');
    };
    new IntersectionObserver(([en]) => { pastButtons = !en.isIntersecting && en.boundingClientRect.top < 0; update(); }).observe(target);
    if (footer) new IntersectionObserver(([en]) => { atFooter = en.isIntersecting; update(); }).observe(footer);
  }
  disconnectedCallback() { setTabbar(true, 'sticky'); }
}
if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

/* ======================= long description: Read more / Read less ======================= */
// Measures with a ResizeObserver, so it also works for a panel that is closed (phone accordion) or a tab
// that is not shown yet: the toggle appears only once the text is really taller than the clamp.
function initClamp(box) {
  const inner = box.querySelector('.c-clamp__inner');
  const btn = box.querySelector('.c-clamp__toggle');
  if (!inner || !btn || box.dataset.ready) return;
  box.dataset.ready = '1';
  const limit = () => parseFloat(getComputedStyle(box).getPropertyValue('--clamp')) || 220;
  const measure = () => {
    if (box.classList.contains('is-open')) return;
    const tall = inner.scrollHeight > limit() + 24; // a few lines of slack: never hide just one line
    box.classList.toggle('is-clamped', tall);
    btn.hidden = !tall;
  };
  const label = btn.querySelector('span');
  btn.addEventListener('click', () => {
    const open = !box.classList.contains('is-open');
    box.classList.toggle('is-open', open);
    box.classList.toggle('is-clamped', !open);
    btn.setAttribute('aria-expanded', open);
    if (label) label.textContent = open ? btn.dataset.less : btn.dataset.more;
    if (!open) {
      const top = box.getBoundingClientRect().top;
      if (top < 0) window.scrollBy({ top: top - 90, behavior: 'smooth' });
    }
  });
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(inner);
  measure();
}

/* ======================= details tabs ======================= */
class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll(':scope > .s-details__panel')];
    this.list = this.querySelector('[role="tablist"]');
    if (!this.list || this.panels.length < 1) return;
    this.list.innerHTML = '';
    this.buttons = this.panels.map((p, i) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'c-tab'; b.setAttribute('role', 'tab'); b.id = `${this.id || 'pt'}-tab-${i}`;
      b.textContent = p.dataset.tab;
      b.addEventListener('click', () => this.activate(i));
      b.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.panels.length) % this.panels.length;
          this.activate(n); this.buttons[n].focus();
        }
      });
      this.list.appendChild(b);
      return b;
    });
    const mq = matchMedia('(min-width: 900px)');
    const apply = () => {
      this.classList.toggle('is-tabbed', mq.matches);
      if (mq.matches) { this.panels.forEach((p) => { p.open = true; }); this.activate(this.current || 0); }
    };
    apply();
    mq.addEventListener('change', apply);
    this.querySelectorAll('[data-clamp]').forEach(initClamp);
  }
  activate(i) {
    this.current = i;
    this.panels.forEach((p, n) => p.classList.toggle('is-active', n === i));
    this.buttons.forEach((b, n) => { b.setAttribute('aria-selected', n === i); b.tabIndex = n === i ? 0 : -1; });
  }
}
if (!customElements.get('product-tabs')) customElements.define('product-tabs', ProductTabs);

/* ======================= recommendations ======================= */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    // the section stays hidden until it has products; a 1px sentinel tells us when it is near the viewport
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.setAttribute('data-layout-ignore', '');
    this.before(sentinel);
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      io.disconnect();
      sentinel.remove();
      this.load();
    }, { rootMargin: '600px 0px' });
    io.observe(sentinel);
  }
  async load() {
    if (this.loadPromise) return this.loadPromise; // the "Pairs well with" row can ask before the scroll trigger does
    this.loadPromise = this.doLoad();
    return this.loadPromise;
  }
  async doLoad() {
    try {
      const res = await fetch(this.dataset.url);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const tpl = doc.querySelector('template[data-recs-content]');
      const html = tpl ? tpl.innerHTML.trim() : '';
      const target = this.querySelector('[data-recs-target]');
      if (!html || !target) { this.hidden = true; return; }
      target.insertAdjacentHTML('beforeend', html);
      this.onLoaded?.();
      this.hidden = false;
    } catch (e) {
      this.hidden = true;
    }
  }
}
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);


/* ======================= "Pairs well with" row ======================= */
// Shown only when the cross-sell row is on the page; tapping it scrolls there (loading it first if needed)
// and, when the block says so, briefly highlights the section.
function initPairs() {
  document.querySelectorAll('[data-pairs]').forEach((row) => {
    const fbt = document.querySelector('[data-cross-sell]');
    if (!fbt) return;
    row.hidden = false;
    row.addEventListener('click', async (e) => {
      e.preventDefault();
      if (fbt.hidden) await fbt.load?.();
      if (fbt.hidden) { row.hidden = true; return; } // nothing to show: the section hides itself
      const target = fbt.closest('.shopify-section') || fbt;
      const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      if (row.dataset.pairsFlash === 'true') {
        fbt.classList.remove('is-flash');
        void fbt.offsetWidth; // restart the animation on repeat taps
        fbt.classList.add('is-flash');
        setTimeout(() => fbt.classList.remove('is-flash'), 1800);
      }
    });
  });
}
initPairs();

/* ======================= quick view ======================= */
let quickDialog;
function ensureProductCss() {
  if (!config.productCss || document.querySelector(`link[href="${config.productCss}"]`)) return;
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = config.productCss; document.head.appendChild(l);
}
window.theme.openQuickView = async function (handle, trigger) {
  ensureProductCss();
  if (!quickDialog) {
    quickDialog = document.createElement('dialog');
    quickDialog.className = 'c-modal c-modal--quick';
    quickDialog.innerHTML = `<div class="c-modal__head"><span></span><button type="button" class="c-icon-btn c-icon-btn--soft" data-modal-close aria-label="${config.strings.close || 'Close'}"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div><div class="c-modal__body" data-modal-body></div>`;
    document.body.appendChild(quickDialog);
    quickDialog.addEventListener('click', (e) => { if (e.target === quickDialog || e.target.closest('[data-modal-close]')) quickDialog.close(); });
    quickDialog.addEventListener('close', () => { quickDialog.querySelector('[data-modal-body]').innerHTML = ''; trigger?.focus?.(); });
    subscribe('cart:add', () => { if (quickDialog.open) quickDialog.close(); });
  }
  trigger?.classList.add('is-loading');
  try {
    const root = (config.routes.root || '/').replace(/\/$/, '');
    const res = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=quick-view`);
    if (!res.ok) throw new Error(res.status);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const content = doc.querySelector('product-info');
    if (!content) throw new Error('empty');
    quickDialog.querySelector('[data-modal-body]').replaceChildren(document.importNode(content, true));
    quickDialog.showModal();
  } catch (e) {
    location.href = `${(config.routes.root || '/').replace(/\/$/, '')}/products/${handle}`;
  } finally {
    trigger?.classList.remove('is-loading');
  }
};
